/**
 * PATH: packages/ui/src/kanban/components/settings-home/FaceSettingsPage.tsx
 * FILE_ID: UI-KANBAN-SETTINGS-FACE-001
 * PURPOSE: Card-face settings — blocks, due chrome, Also-show place, density default.
 * ROLE IN ARCHITECTURE: Settings-home page body. Reuses CardFaceCustomizePanel.
 * MAIN EXPORTS: FaceSettingsPage
 * NON-RESPONSIBILITIES: Does not mint a prefs API. Density only on All Kanban.
 * NOTES FOR FUTURE AI: Also-show place is allocator. The menu switch writes
 *   menu.alsoShow. The card reads both: allocator must be menu and alsoShow on.
 */
import { CardFaceCustomizePanel } from '../board-view/CardFaceCustomizePanel'
import {
  CARD_DENSITY_CYCLE,
  CARD_DENSITY_LABEL,
  type CardDensity,
} from '../board-view/cardDensity'
import {
  patchCardFaceAllBoards,
  patchCardFaceBoard,
  resolveCardFacePrefs,
  type KanbanCardFacePrefs,
} from '../board-view/cardFacePrefs'
import type { Board } from '../../types'
import { FaceRecipeSettings } from './FaceRecipeSettings'
import { APPLY_TO_OPTIONS, OptionStrip, SectionShell } from './chrome'
import { SETTINGS_TEST_IDS } from './settingsTestIds'
import type { SettingsPage } from './nav'
import { faceScopeFor } from './nav'

const ALLOCATOR_OPTIONS = [
  { id: 'rail' as const, title: 'On rail', description: 'Also-show icon on the card capsule.' },
  { id: 'menu' as const, title: 'More menu', description: 'Also-show stays in the menu and right-click.' },
  { id: 'off' as const, title: 'Off', description: 'Hidden from the capsule and the menu.' },
]

export function FaceSettingsPage({
  page,
  onPage,
  board,
  prefs,
  setPrefs,
  cardDensity,
  setCardDensity,
  allowBoardScope = true,
}: {
  page: SettingsPage
  onPage: (page: SettingsPage) => void
  board?: Board
  prefs: KanbanCardFacePrefs
  setPrefs: (next: KanbanCardFacePrefs) => void
  cardDensity: CardDensity
  setCardDensity: (next: CardDensity) => void
  allowBoardScope?: boolean
}) {
  const scope = allowBoardScope ? faceScopeFor(page) : 'all'
  const showDensity = page === 'kanban-face'
  const resolved = resolveCardFacePrefs(
    prefs,
    scope === 'board' && board ? { boardId: board.id, boardKind: board.kind } : undefined,
  )
  const view: KanbanCardFacePrefs = {
    ...prefs,
    order: resolved.order,
    hidden: resolved.hidden,
    fields: resolved.fields,
    place: resolved.place,
    strip: resolved.strip,
    menu: resolved.menu,
    extras: resolved.extras,
    compose: resolved.compose,
    railMode: resolved.railMode,
    tileBody: resolved.tileBody,
    tileOpen: resolved.tileOpen,
    complete: resolved.complete,
  }

  const writeView = (next: KanbanCardFacePrefs) => {
    const storedTile =
      scope === 'board' && board
        ? prefs.boardOverrides[board.id]?.tileBody
        : prefs.tileBody
    const tileChanged = next.tileBody !== (storedTile ?? resolved.tileBody)
    const patch = {
      order: next.order,
      hidden: next.hidden,
      fields: next.fields,
      place: next.place,
      strip: next.strip,
      menu: next.menu,
      extras: next.extras,
      compose: next.compose,
      railMode: next.railMode,
      tileOpen: next.tileOpen,
      complete: next.complete,
      ...(tileChanged ? { tileBody: next.tileBody } : {}),
    }
    if (scope === 'board' && board) {
      setPrefs(patchCardFaceBoard(prefs, board.id, patch))
      return
    }
    setPrefs(patchCardFaceAllBoards(prefs, patch))
  }

  return (
    <div data-testid="settings-face-page">
      {allowBoardScope ? (
        <SectionShell
          title="Applies to"
          hint="Same split as Card drawer. The menu on the right stays in sync."
        >
          <OptionStrip
            label="Where this tile recipe is used"
            value={scope}
            options={APPLY_TO_OPTIONS}
            testId="settings-face-apply"
            onChange={(id) => onPage(id === 'all' ? 'kanban-face' : 'board-face')}
          />
        </SectionShell>
      ) : null}
      {showDensity && (
        <SectionShell
          title="Default card size"
          hint="Boards without a face override use this size. Do not add a second density row on Board."
        >
          <div className="mb-2" data-testid={SETTINGS_TEST_IDS.faceDensity}>
            <div className="mb-1 text-xs text-neutral-600 dark:text-zinc-300">Card density</div>
            <div className="inline-flex overflow-hidden rounded-md border border-neutral-200 dark:border-zinc-800">
              {CARD_DENSITY_CYCLE.map((id) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setCardDensity(id)}
                  aria-pressed={cardDensity === id}
                  className={[
                    'px-2.5 py-1 text-[11px]',
                    cardDensity === id
                      ? 'bg-neutral-800 text-white dark:bg-zinc-200 dark:text-zinc-900'
                      : 'bg-white text-neutral-600 hover:bg-neutral-100 dark:bg-zinc-950 dark:text-zinc-300 dark:hover:bg-zinc-900',
                  ].join(' ')}
                >
                  {CARD_DENSITY_LABEL[id]}
                </button>
              ))}
            </div>
          </div>
        </SectionShell>
      )}
      <SectionShell title="On the tile" hint="Title cannot be removed. Hide omits chrome, never card data.">
        <CardFaceCustomizePanel prefs={view} setPrefs={writeView} />
      </SectionShell>
      <SectionShell title="Due date" hint="Hide removes the due pill. The date stays on the card.">
        <label className="flex items-center justify-between gap-3 py-1 text-xs text-neutral-600 dark:text-zinc-300">
          Show due on the tile
          <button
            type="button"
            role="switch"
            data-testid={SETTINGS_TEST_IDS.faceDue}
            aria-checked={resolved.fields.due}
            onClick={() =>
              writeView({
                ...view,
                fields: { ...view.fields, due: !resolved.fields.due },
              })
            }
            className={[
              'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors',
              resolved.fields.due ? 'bg-blue-600' : 'bg-neutral-300 dark:bg-zinc-700',
            ].join(' ')}
          >
            <span
              className={[
                'inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform',
                resolved.fields.due ? 'translate-x-4' : 'translate-x-0.5',
              ].join(' ')}
            />
          </button>
        </label>
      </SectionShell>
      <SectionShell
        title="Also-show"
        hint="Link, not Move or Copy. On rail / More menu / Off."
      >
        <OptionStrip
          label="Where Also-show lives"
          value={resolved.place.allocator ?? 'menu'}
          options={ALLOCATOR_OPTIONS}
          testId={SETTINGS_TEST_IDS.faceAllocator}
          onChange={(id) =>
            writeView({
              ...view,
              place: { ...view.place, allocator: id },
            })
          }
          />
        </SectionShell>
      <FaceRecipeSettings view={view} resolved={resolved} writeView={writeView} />
    </div>
  )
}
